import {
  BidStatus,
  EmailInviteDto,
  EmailInviteStatusDto,
  NegotiationRoundStatus,
  NegotiationRoundDto,
  OrganizationInviteStatusDto,
  ReceivedBidDto,
  SourcingEventDto,
  UninvitedPanelSupplierDto,
} from "../../autogen/bff-api";
import { invitedOrganizations } from "./invitedOrganizations";

export type RoundAnswer =
  | "NewBid"
  | "ConfirmedStandingBid"
  | "Declined"
  | "NoAnswer"
  | "Awaiting"
  | "Rejected"
  | "NotInDialogue"
  | "DeclinedStaysOnTender"
  | "NoAnswerStaysOnTender";

export interface SupplierRow {
  organizationId: string;
  organizationName: string;
  registeredAt?: string;
  inviteStatus?: OrganizationInviteStatusDto;
  emailInvites?: EmailInviteDto[];
  uninvitedPanelMember?: UninvitedPanelSupplierDto;
  roundAnswer?: RoundAnswer;
  bid?: ReceivedBidDto;
}

export const buildSupplierRows = (
  event: SourcingEventDto,
  bids: ReceivedBidDto[],
  unregisteredPanel: UninvitedPanelSupplierDto[] = [],
): SupplierRow[] => {
  const byOrg = new Map<string, SupplierRow>();

  for (const registration of event.supplierRegistrations ?? []) {
    byOrg.set(registration.organizationId, {
      organizationId: registration.organizationId,
      organizationName: registration.organizationName,
      registeredAt: registration.registeredAt,
    });
  }

  for (const invite of invitedOrganizations(event)) {
    byOrg.set(invite.organizationId, {
      organizationId: invite.organizationId,
      organizationName: invite.organizationName ?? "",
      inviteStatus: invite.status ?? OrganizationInviteStatusDto.Invited,
    });
  }

  for (const [entryKey, invites] of emailInvitesBySupplier(event)) {
    byOrg.set(entryKey, {
      organizationId: entryKey,
      organizationName: invites[0].organizationEntry?.name ?? invites[0].email,
      emailInvites: invites,
    });
  }

  for (const member of panelMembersWithoutEmailInvite(event, unregisteredPanel)) {
    byOrg.set(member.organizationEntryId, {
      organizationId: member.organizationEntryId,
      organizationName: member.name,
      uninvitedPanelMember: member,
    });
  }

  for (const bid of bids) {
    const existing = byOrg.get(bid.owner);
    if (existing) {
      existing.bid = bid;
    } else {
      byOrg.set(bid.owner, {
        organizationId: bid.owner,
        organizationName: bid.ownerName,
        bid,
      });
    }
  }

  return Array.from(byOrg.values()).sort((a, b) =>
    a.organizationName.localeCompare(b.organizationName),
  );
};

const emailInvitesBySupplier = (event: SourcingEventDto): Map<string, EmailInviteDto[]> => {
  const invites = event.eventContext.type === "MiniCompetition" ? event.eventContext.emailInvites ?? [] : [];
  const bySupplier = new Map<string, EmailInviteDto[]>();
  for (const invite of invites) {
    const key = invite.organizationEntry?.id ?? invite.id;
    bySupplier.set(key, [...(bySupplier.get(key) ?? []), invite]);
  }
  return bySupplier;
};

export const panelMembersWithoutEmailInvite = (
  event: SourcingEventDto,
  unregisteredPanel: UninvitedPanelSupplierDto[],
): UninvitedPanelSupplierDto[] => {
  const emailed = emailInvitesBySupplier(event);
  return unregisteredPanel.filter((member) => !emailed.has(member.organizationEntryId));
};

const EMAIL_INVITE_PROGRESS = [
  EmailInviteStatusDto.Opened,
  EmailInviteStatusDto.Sent,
  EmailInviteStatusDto.Pending,
  EmailInviteStatusDto.SendingFailed,
];

export const furthestEmailInvite = (invites: EmailInviteDto[]): EmailInviteDto =>
  [...invites].sort(
    (a, b) => EMAIL_INVITE_PROGRESS.indexOf(a.status) - EMAIL_INVITE_PROGRESS.indexOf(b.status),
  )[0];

export const buildRoundSupplierRows = (
  round: NegotiationRoundDto,
  bids: ReceivedBidDto[],
  rejectedOrganizationIds: ReadonlySet<string> = new Set(),
  now: number = Date.now(),
  partIIStandingTenders?: readonly ReceivedBidDto[],
): SupplierRow[] => {
  const roundClosed =
    round.status === NegotiationRoundStatus.Completed || new Date(round.deadline).getTime() <= now;
  const standingOf = (organizationId: string) => partIIStandingTenders?.find((bid) => bid.owner === organizationId);
  const invited: SupplierRow[] = round.invites.map((invite) => {
    const roundBid = bids.find((candidate) => candidate.owner === invite.organizationId);
    const answer = rejectedOrganizationIds.has(invite.organizationId)
      ? "Rejected"
      : roundAnswerOf(roundBid, invite.declinedAt != null, roundClosed);
    const staysOnTender = partIIStandingTenders != null && (answer === "Declined" || answer === "NoAnswer");
    return {
      organizationId: invite.organizationId,
      organizationName: invite.organizationName,
      roundAnswer: staysOnTender ? (answer === "Declined" ? "DeclinedStaysOnTender" : "NoAnswerStaysOnTender") : answer,
      bid: roundBid ?? (staysOnTender ? standingOf(invite.organizationId) : undefined),
    };
  });
  const invitedIds = new Set(round.invites.map((invite) => invite.organizationId));
  const leftOut: SupplierRow[] = (partIIStandingTenders ?? [])
    .filter((bid) => !invitedIds.has(bid.owner))
    .map((bid) => ({
      organizationId: bid.owner,
      organizationName: bid.ownerName,
      roundAnswer: rejectedOrganizationIds.has(bid.owner) || bid.status === BidStatus.Rejected ? "Rejected" : "NotInDialogue",
      bid,
    }));
  return [...invited, ...leftOut].sort((a, b) => a.organizationName.localeCompare(b.organizationName));
};

const roundAnswerOf = (bid: ReceivedBidDto | undefined, declined: boolean, roundClosed: boolean): RoundAnswer => {
  if (bid && bid.status !== BidStatus.Draft) return bid.confirmsStandingBid ? "ConfirmedStandingBid" : "NewBid";
  if (declined) return "Declined";
  return roundClosed ? "NoAnswer" : "Awaiting";
};

export const isBidDeadlinePassed = (event: SourcingEventDto): boolean => {
  const deadline = event.milestoneDates?.bidSubmissionDeadline;
  return deadline != null && new Date(deadline).getTime() <= Date.now();
};
